<script setup lang="ts">
/*
 * Компонент EmployeeSelectionModal - модальное окно выбора сотрудника с древовидной структурой
 */

import { computed, ref, watch } from 'vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'

import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

interface Props {
  employees: IEmployeeTreeDepartment[] // Иерархический массив подразделений с сотрудниками
  modelValue?: string | null // Выбранный ID сотрудника (v-model)
  placeholder?: string // Плейсхолдер для поля поиска
  title?: string // Заголовок модального окна
  multiple?: boolean // Режим множественного выбора
  selectedEmployees?: IEmployeeTreeItem[] // Предварительно выбранные сотрудники
  applyLabel?: string // Текст кнопки подтверждения
  secondaryLabel?: string // Текст вторичной кнопки
  secondaryAction?: 'close' | 'clear' // Действие вторичной кнопки
  dialogMaxWidth?: number | string // Ширина модального окна
  compactHeader?: boolean // Компактный заголовок
  filtered?: boolean // Передан отфильтрованный список сотрудников
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'change', value: string | null): void
  (e: 'select', employee: IEmployeeTreeItem): void
  (e: 'select-multiple', employees: IEmployeeTreeItem[]): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Выберите сотрудника',
  placeholder: 'Поиск по сотруднику...',
  multiple: false,
  selectedEmployees: undefined,
  applyLabel: 'Выбрать',
  secondaryLabel: 'Отмена',
  secondaryAction: 'close',
  dialogMaxWidth: 600,
  compactHeader: false,
  filtered: false,
})

const emit = defineEmits<Emits>()
const apiStore = useApiStore()

// Реактивные состояния
const search = ref('')
const searchDebounced = refDebounced(search, 300)
const isOpen = ref(false)
const openedDepts = ref<string[]>([])
const selectedEmployeesSet = ref<Set<string>>(new Set())
const localSelectedEmployees = ref<IEmployeeTreeItem[]>([])

const isEmployee = (item: IEmployeeTreeDepartment | IEmployeeTreeItem | undefined): item is IEmployeeTreeItem => {
  if (!item) return false
  return 'full_name' in item
}

const getItemChildren = (item: Record<string, unknown> | undefined) => {
  if (!item) return null
  if ('full_name' in item) return null

  const children: (IEmployeeTreeDepartment | IEmployeeTreeItem)[] = []

  const employees = item.employees
  if (Array.isArray(employees) && employees.length > 0) {
    children.push(...(employees as IEmployeeTreeItem[]))
  }

  const childrenDepts = item.children
  if (Array.isArray(childrenDepts) && childrenDepts.length > 0) {
    children.push(...(childrenDepts as IEmployeeTreeDepartment[]))
  }

  return children.length > 0 ? children : null
}

const hideModal = () => {
  setTimeout(() => {
    isOpen.value = false
  }, 0)
}

const highlightMatch = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) return text
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

const resetSearch = () => {
  search.value = ''
}

// Формирование дерева для рендеринга (ленивая обрезка для оптимизации DOM и фильтрация при поиске)
const filteredEmployeesTree = computed<(IEmployeeTreeDepartment | IEmployeeTreeItem)[]>(() => {
  if (!props.employees || props.employees.length === 0) return []
  const query = searchDebounced.value.toLowerCase()

  if (query.length < 3) {
    /*
      Ленивое формирование дерева: отдаем Vuetify только раскрытые ветки.
      Свернутые отделы заменяются на dummy-заглушки, чтобы Vuetify отрисовывал стрелку раскрытия,
      но не грузил в DOM сотни скрытых сотрудников (это снижает время рендеринга с 3 сек до 0.1 сек).
    */
    const makeLazy = (dept: IEmployeeTreeDepartment): IEmployeeTreeDepartment => {
      const isOpened = openedDepts.value.includes(dept.id)
      if (!isOpened) {
        const hasChildren = (dept.children && dept.children.length > 0) || (dept.employees && dept.employees.length > 0)
        return {
          ...dept,
          employees: [],
          children: hasChildren ? [{ id: `dummy-${dept.id}` } as any] : [],
        }
      }
      return {
        ...dept,
        employees: dept.employees || [],
        children: (dept.children || []).map(child => makeLazy(child)),
      }
    }
    return props.employees.map(dept => makeLazy(dept))
  }

  const matchesQuery = (text: string) => text.toLowerCase().includes(query)

  const filterDept = (dept: IEmployeeTreeDepartment): IEmployeeTreeDepartment | null => {
    // Если само подразделение совпадает с запросом, возвращаем его целиком
    if (matchesQuery(dept.name)) {
      return dept
    }

    const matchedEmployees = dept.employees
      ? dept.employees.filter(emp => matchesQuery(emp.full_name))
      : []

    const matchedChildren: IEmployeeTreeDepartment[] = []
    if (dept.children && dept.children.length > 0) {
      for (const child of dept.children) {
        const filteredChild = filterDept(child)
        if (filteredChild) {
          matchedChildren.push(filteredChild)
        }
      }
    }

    if (matchedEmployees.length > 0 || matchedChildren.length > 0) {
      return {
        ...dept,
        employees: matchedEmployees,
        children: matchedChildren,
      }
    }

    return null
  }

  const result: IEmployeeTreeDepartment[] = []
  for (const dept of props.employees) {
    const filtered = filterDept(dept)
    if (filtered) {
      result.push(filtered)
    }
  }
  return result
})

const isEmployeeSelected = (employeeId: string) => {
  return selectedEmployeesSet.value.has(employeeId)
}

const handleSelect = (employee: IEmployeeTreeItem) => {
  if (props.multiple) {
    if (selectedEmployeesSet.value.has(employee.id)) {
      selectedEmployeesSet.value.delete(employee.id)
      localSelectedEmployees.value = localSelectedEmployees.value.filter(
        emp => emp.id !== employee.id,
      )
    } else {
      selectedEmployeesSet.value.add(employee.id)
      localSelectedEmployees.value.push(employee)
    }
  } else {
    emit('update:modelValue', employee.id)
    emit('change', employee.id)
    emit('select', employee)
    hideModal()
    resetSearch()
  }
}

const handleSecondaryClick = () => {
  if (props.secondaryAction === 'clear') {
    localSelectedEmployees.value = []
    selectedEmployeesSet.value.clear()
  } else {
    closeModal()
  }
}

const applyMultipleSelection = () => {
  emit('select-multiple', [...localSelectedEmployees.value])
  hideModal()
  resetSearch()
}

const closeModal = () => {
  hideModal()
  resetSearch()
  if (props.multiple) {
    localSelectedEmployees.value = []
    selectedEmployeesSet.value.clear()
  }
  emit('close')
}

const getAllEmployeesInDept = (dept: IEmployeeTreeDepartment): IEmployeeTreeItem[] => {
  const list: IEmployeeTreeItem[] = []
  if (dept.employees && Array.isArray(dept.employees)) {
    list.push(...dept.employees)
  }
  if (dept.children && Array.isArray(dept.children)) {
    for (const child of dept.children) {
      list.push(...getAllEmployeesInDept(child))
    }
  }
  return list
}

const getAllDeptIds = (dept: IEmployeeTreeDepartment): string[] => {
  const ids: string[] = [dept.id]
  if (dept.children && Array.isArray(dept.children)) {
    for (const child of dept.children) {
      ids.push(...getAllDeptIds(child))
    }
  }
  return ids
}

const handleDeptClick = (event: Event, dept: IEmployeeTreeDepartment) => {
  if (!props.multiple) return
  event.stopPropagation()

  const employees = getAllEmployeesInDept(dept)
  if (employees.length === 0) return

  const allSelected = employees.every(emp => selectedEmployeesSet.value.has(emp.id))

  if (allSelected) {
    employees.forEach(emp => {
      selectedEmployeesSet.value.delete(emp.id)
    })
    localSelectedEmployees.value = localSelectedEmployees.value.filter(
      emp => !employees.some(e => e.id === emp.id),
    )
  } else {
    employees.forEach(emp => {
      if (!selectedEmployeesSet.value.has(emp.id)) {
        selectedEmployeesSet.value.add(emp.id)
        localSelectedEmployees.value.push(emp)
      }
    })

    // Автоматически раскрываем все подотделы в этой ветке
    const deptIds = getAllDeptIds(dept)
    deptIds.forEach(id => {
      if (!openedDepts.value.includes(id)) {
        openedDepts.value.push(id)
      }
    })
  }
}

// Инициализация раскрытых узлов по умолчанию (оставляем свернутыми для быстрой начальной отрисовки)
const initializeExpanded = () => {
  openedDepts.value = []
}

const openModal = async () => {
  if (!props.filtered && !apiStore.employees?.length) {
    await apiStore.fetchAllEmployees()
  }

  initializeExpanded()

  if (props.multiple && props.selectedEmployees && props.selectedEmployees.length > 0) {
    localSelectedEmployees.value = [...props.selectedEmployees]
    selectedEmployeesSet.value = new Set(props.selectedEmployees.map(emp => emp.id))
  } else {
    localSelectedEmployees.value = []
    selectedEmployeesSet.value.clear()
  }

  isOpen.value = true
}

defineExpose({
  open: openModal,
  close: closeModal,
})

const selectedItemsData = computed(() => {
  return localSelectedEmployees.value.map(emp => ({
    id: emp.id,
    name: emp.full_name,
    description: emp.position_name,
  }))
})

const removeEmployee = (id: string | number) => {
  const idStr = String(id)
  selectedEmployeesSet.value.delete(idStr)
  localSelectedEmployees.value = localSelectedEmployees.value.filter(emp => emp.id !== idStr)
}

const removeAllEmployees = () => {
  selectedEmployeesSet.value.clear()
  localSelectedEmployees.value = []
}

watch(() => props.employees, () => {
  initializeExpanded()
}, { immediate: true })

watch(searchDebounced, newVal => {
  if (newVal && newVal.trim().length >= 3) {
    const allDeptIds: string[] = []
    const collectDeptIds = (depts: (IEmployeeTreeDepartment | IEmployeeTreeItem)[]) => {
      for (const d of depts) {
        if (!isEmployee(d)) {
          allDeptIds.push(d.id)
          if (d.children && d.children.length > 0) {
            collectDeptIds(d.children)
          }
        }
      }
    }
    collectDeptIds(filteredEmployeesTree.value)
    openedDepts.value = allDeptIds
  } else {
    initializeExpanded()
  }
})

const modalTitle = computed(() => {
  if (props.title !== 'Выберите сотрудника') {
    return props.title
  }
  return props.multiple ? 'Выберите сотрудников' : 'Выберите сотрудника'
})

const modalPlaceholder = computed(() => {
  if (props.placeholder !== 'Поиск по сотруднику...') {
    return props.placeholder
  }
  return props.multiple ? 'Поиск по сотрудникам (от 3 символов)' : 'Поиск по сотруднику (от 3 символов)'
})
</script>

<template>
  <VDialog
    v-model="isOpen"
    :max-width="dialogMaxWidth"
    max-height="90vh"
    content-class="entity-form-menu"
    @click:outside="closeModal"
  >
    <VCard
      class="pa-4 d-flex flex-column ga-2"
      style="max-height: 100%; min-height: 0;"
    >
      <!-- Шапка -->
      <div class="d-flex align-center justify-space-between">
        <span class="text-h4">{{ modalTitle }}</span>
        <VBtn
          icon
          variant="text"
          size="small"
          @click="closeModal"
        >
          <VIcon icon="ri-close-line" />
        </VBtn>
      </div>

      <!-- Поиск -->
      <div class="d-flex flex-column ga-1">
        <div class="d-flex ga-2 align-center">
          <VTextField
            v-model="search"
            variant="outlined"
            clearable
            :append-inner-icon="search ? undefined : 'ri-search-line'"
            autofocus
            :placeholder="modalPlaceholder"
            rounded="pill"
            class="flex-grow-1 search-field"
            @click:clear="resetSearch"
          />
        </div>

        <!-- Оверлей выбранных сотрудников (при наведении) -->
        <div v-if="multiple" :style="{ visibility: localSelectedEmployees.length === 0 ? 'hidden' : 'visible' }">
          <VMenu
            open-on-hover
            :close-on-content-click="false"
            close-delay="200"
            location="bottom start"
            offset="8"
            content-class="date-picker-menu"
          >
            <template #activator="{ props: menuProps }">
              <span
                v-bind="menuProps"
                class="text-body-1 text-medium-emphasis cursor-pointer d-inline-block"
              >
                Выбрано пользователей: {{ localSelectedEmployees.length }}
              </span>
            </template>

            <CustomSelectedItems
              :items="selectedItemsData"
              :title="`Выбрано ${localSelectedEmployees.length}:`"
              @remove="removeEmployee"
              @remove-all="removeAllEmployees"
            />
          </VMenu>
        </div>
      </div>

      <!-- Дерево сотрудников -->
      <div class="flex-grow-1 overflow-y-auto min-h-0 custom-treeview-container">
        <VTreeview
          v-if="filteredEmployeesTree.length > 0"
          v-model:opened="openedDepts"
          :items="filteredEmployeesTree"
          :item-children="getItemChildren"
          item-value="id"
          :open-on-click="!multiple"
          class="custom-treeview"
        >
          <template #toggle="{ props: toggleProps, item }">
            <VBtn
              v-if="!isEmployee(item)"
              v-bind="toggleProps"
              variant="text"
              density="compact"
              icon
              class="expand-btn"
              :class="{ expanded: openedDepts.some(id => String(id) === String(item.id)) }"
            >
              <VIcon
                icon="ri-play-fill"
                size="16"
                class="expand-icon"
              />
            </VBtn>
            <div v-else class="node-leaf-spacer" />
          </template>

          <template #title="{ item }">
            <template v-if="item">
              <!-- Подразделение -->
              <div
                v-if="!isEmployee(item)"
                class="w-100 text-body-1 text-high-emphasis py-1 px-2 rounded-md"
                :class="{ 'cursor-pointer': multiple }"
                @click="handleDeptClick($event, item)"
                v-html="highlightMatch(item.name, searchDebounced)"
              />

              <!-- Сотрудник -->
              <div
                v-else
                class="d-flex align-center w-100 cursor-pointer py-1 px-2 rounded-md transition-colors"
                :class="isEmployeeSelected(item.id) ? 'selected-employee text-primary' : 'text-high-emphasis'"
                @click.stop="handleSelect(item)"
              >
                <div class="node-content d-flex flex-column flex-grow-1 ga-1" style="min-width: 0;">
                  <div class="text-body-1 text-high-emphasis" v-html="highlightMatch(item.full_name, searchDebounced)" />
                  <div
                    v-if="item.position_name"
                    class="text-subtitle-2 text-medium-emphasis"
                    v-html="highlightMatch(item.position_name, searchDebounced)"
                  />
                </div>
              </div>
            </template>
          </template>
        </VTreeview>

        <!-- Сообщение если ничего не найдено -->
        <div
          v-else
          class="d-flex flex-column align-center justify-center ga-2 pa-8 text-medium-emphasis"
        >
          <VIcon
            icon="ri-search-line"
            size="48"
          />
          <div class="text-body-1">
            Сотрудники не найдены
          </div>
          <div class="text-caption text-medium-emphasis">
            Попробуйте изменить поисковый запрос
          </div>
        </div>
      </div>

      <!-- Кнопки действий для множественного выбора -->
      <div
        v-if="multiple"
        class="d-flex ga-3"
      >
        <VBtn
          variant="outlined"
          color="secondary"
          class="flex-grow-1 text-none"
          @click="handleSecondaryClick"
        >
          {{ secondaryLabel }}
        </VBtn>
        <VBtn
          color="primary"
          class="flex-grow-1 text-none"
          :disabled="localSelectedEmployees.length === 0"
          @click="applyMultipleSelection"
        >
          {{ applyLabel }}
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.search-highlight) {
  background-color: rgba(var(--v-theme-primary), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
  color: inherit;
}

:deep(.search-field.v-text-field .v-field) {
  border-radius: 500px !important;
}

.custom-treeview {
  --indent-level: 0;

  :deep(.v-list-item-title),
  :deep(.v-list-item__content) {
    white-space: normal !important;
    word-break: break-word !important;
  }

  :deep(.v-list-item) {
    padding-inline-start: calc(var(--indent-level) * 34px) !important;
    border-radius: 6px !important;
    height: auto !important;
    min-height: 40px !important;
    align-items: center !important;
  }

  :deep(.v-treeview-group.v-list-group .v-list-group__items .v-list-item) {
    padding-inline-start: calc(var(--indent-level) * 34px) !important;
  }

  :deep(.v-list-group__items) {
    --indent-level: 1;
  }

  :deep(.v-list-group__items .v-list-group__items) {
    --indent-level: 2;
  }

  :deep(.v-list-group__items .v-list-group__items .v-list-group__items) {
    --indent-level: 3;
  }

  :deep(.v-list-item__prepend) {
    width: 24px !important;
    min-width: 24px !important;
    margin-inline-end: 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :deep(.v-list-item-action) {
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
  }

  :deep(.v-list-item__spacer) {
    display: none !important;
  }
}

.custom-treeview-container {
  padding-inline-end: 8px;
}

.node-leaf-spacer {
  width: 24px !important;
  min-width: 24px !important;
  height: 24px !important;
}

.expand-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  padding: 0 !important;

  .v-icon {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: rotate(0deg);
  }

  &.expanded .v-icon {
    transform: rotate(90deg) !important;
  }
}

.selected-employee {
  background: rgba(var(--v-theme-primary), var(--v-activated-opacity)) !important;
  border-radius: 6px !important;
}
</style>
